import { Component, Input, Output, EventEmitter } from '@angular/core';
import { SkillAcknowledgementResponse } from '../../models/skill-acknowledgement-response';
import { PanelList } from '../../../shared/models/panel-list';
import { Router } from '@angular/router';
import { CommonService } from '../../../shared/services/common.service';
import { MySkillAcknowledgementsService } from '../../services/my-skill-acknowledgements.service';
import { FilterMenu } from '../../../shared/models/filter-menu';
import { SkillAcknowledgementRequestDto } from '../../models/skill-acknowledgement-request-dto';
import { MySkillAcknowledgementsConstants } from '../../constants/my-skill-acknowledgements.constants';
import { SortMenu } from '../../../shared/models/sort-menu';
import { MessageService } from 'primeng/api';
import { delay } from 'rxjs/operators';

@Component({
  selector: 'app-my-skill-acknowledgements-list',
  templateUrl: './my-skill-acknowledgements-list.component.html',
  styleUrls: ['./my-skill-acknowledgements-list.component.scss']
})
export class MySkillAcknowledgementsListComponent {
  @Input() showLoader: boolean = false;
  @Input() skillAcknowledgements: SkillAcknowledgementResponse[] | undefined = undefined;
  @Input() isShowCheckbox: boolean = false;
  @Input() selectCheckbox: boolean = false;
  @Input() showInformationIconCheckbox: boolean = false;
  @Input() enableInactiveLabel: boolean = false;
  @Input() checkboxLabel: string = 'Show All';
  @Input() informationIconTooltip: string | undefined = undefined;

  @Output() checkboxSelection = new EventEmitter<boolean>();
  @Output() reloadMySkillAcknowledgements = new EventEmitter<boolean>();

  skillAcknowledgementPanelList: PanelList = {
    title: undefined,
    lists: [],
    enableFilter: true,
    enableSort: true,
    filters: [],
    sort: [],
    enableSelection: true,
    isCheckBoxContainsDisable: true,
    enableToggling: false
  };

  noData: boolean = false;
  selectedPanels: SkillAcknowledgementResponse[] = [];
  isAcknowledgeBtnDisable: boolean = true;
  pendingAcknowledgementStatus: string = 'PendingAcknowledgement';

  constructor(
    private router: Router,
    private messageService: MessageService,
    private commonService: CommonService,
    private mySkillAcknowledgementsService: MySkillAcknowledgementsService) {
  }
  ngOnChanges(): void {
    this.setPanelList();
  }

  private isAcknowledgementRequired(skill: SkillAcknowledgementResponse): boolean {
    const requiredValue = (skill as any)?.acknowledgementRequired;

    if (typeof requiredValue === 'string') {
      return requiredValue.trim().toLowerCase() === 'true';
    }
    if (requiredValue === true) {
      return true;
    }
    return !skill.acknowledgementShowAllFlag;
  }

  private isRowSelectableForAcknowledgement(skill: SkillAcknowledgementResponse): boolean {
    return this.isAcknowledgementRequired(skill) && this.isPendingAcknowledgement(skill);
  }

  setPanelList(): void {
    this.skillAcknowledgementPanelList.lists = (this.skillAcknowledgements ?? []).map(skill => ({
      ...skill,
      statusDisplay:this.displayStatus(skill.status),
      isActive: this.isRowSelectableForAcknowledgement(skill) ? 'Yes' : 'No'
    }));
    this.skillAcknowledgementPanelList.filters = this.getFiltersForSkillAcknowledgementPanelList();
    this.skillAcknowledgementPanelList.sort = this.getSortFieldsForSkillAcknowledgementPanelList();
    this.noData = this.commonService.IsListEmpty(this.skillAcknowledgementPanelList.lists);
    this.selectedPanels = [];
    this.isAcknowledgeBtnDisable = true;
    this.skillAcknowledgementPanelList = { ...this.skillAcknowledgementPanelList, clearSelection: true };
  }

  getSelectedItems(selectedItemsData: SkillAcknowledgementResponse[]): void {
    this.selectedPanels = selectedItemsData;
    this.isAcknowledgeBtnDisable = !selectedItemsData.some(item => this.isPendingAcknowledgement(item));
  }

  checkboxSelectionChange(): void {
    this.checkboxSelection.emit(this.selectCheckbox);
  }

  acknowledgeSkills(): void {
    const pendingSkills = this.selectedPanels.filter(item => this.isPendingAcknowledgement(item));

    if (!pendingSkills.length) {
      this.isAcknowledgeBtnDisable = true;
      return;
    }

    const request: SkillAcknowledgementRequestDto = {
      skillCodes: pendingSkills.map(skill => skill.skillCode)
    };
    this.showLoader = true;
    this.mySkillAcknowledgementsService.acknowledgeSkills(request).subscribe({
      next: (response) => {
        if (response?.success === false) {
          this.showLoader = false;
          this.messageService.add({
            severity: MySkillAcknowledgementsConstants.SkillAcknowledgement_Severity_Err,
            summary: MySkillAcknowledgementsConstants.SkillAcknowledgement_Acknowledge_Error,
            detail: response?.errorMessage,
            sticky: true
          });
          return;
        }

        this.messageService.add({
          severity: MySkillAcknowledgementsConstants.SkillAcknowledgement_Severity_Success,
          summary: MySkillAcknowledgementsConstants.SkillAcknowledgement_Acknowledge_Success,
          life: MySkillAcknowledgementsConstants.Toast_Message_Life
        });

        this.selectedPanels = [];
        this.reloadMySkillAcknowledgements.emit(this.selectCheckbox);
      },
      error: (error) => {
        this.showLoader = false;
        this.messageService.add({
          severity: MySkillAcknowledgementsConstants.SkillAcknowledgement_Severity_Err,
          summary: error?.error?.message ?? MySkillAcknowledgementsConstants.SkillAcknowledgement_Acknowledge_Error,
          sticky: true
        });
      }
    });
  }

  viewSkillDetails(skillCode: string): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/eqms.skillDetails/' + skillCode])
    );

    window.open(url, '_blank');
  }

  displayStatus(status: string): string {
    if (status === 'PendingAcknowledgement') {
      return MySkillAcknowledgementsConstants.PendingAcknowledgement;
    }

    if (status === 'PendingVerification') {
      return MySkillAcknowledgementsConstants.PendingVerification;
    }

    return status;
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'PendingAcknowledgement':
        return 'pending-acknowledgement';
      case 'PendingVerification':
        return 'pending-verification';
      case 'Earned':
        return 'earned';
      case 'Removed':
        return 'removed';
      default:
        return '';
    }
  }


  isPendingAcknowledgement(skill: SkillAcknowledgementResponse): boolean {
    return skill.status === this.pendingAcknowledgementStatus;
  }

  getSortFieldsForSkillAcknowledgementPanelList(): SortMenu[] {
    return [
      {
        sortFieldDisplayName: 'Skill',
        sortFieldName: 'skillCode'
      },
      {
        sortFieldDisplayName: 'Skill Name',
        sortFieldName: 'skillName'
      },
      {
        sortFieldDisplayName: 'Status',
        sortFieldName: 'status'
      },
      {
        sortFieldDisplayName: 'Date',
        sortFieldName: 'date'
      }      
    ];
  }

  getFiltersForSkillAcknowledgementPanelList(): FilterMenu[] {
    return [
      {
        filterDisplayName: 'Status',
        filterFieldName: 'statusDisplay'
      }
    ];
  }

}
